import React, { Fragment, PureComponent } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { RateTimePeriod } from '~/ExchangeRate';
import SaveActivityIndicator from '~/ProductGroupDetail/SaveActivityIndicator';
import ProductGroupDownloadPopover from '~/ProductGroupDownloadPopover';
import ProductGroupOptionsPopover from '~/ProductGroupOptionsPopover';
import Breadcrumbs from '~/UI/Breadcrumbs/Breadcrumbs';
import BreadcrumbsItem from '~/UI/Breadcrumbs/BreadcrumbsItem';
import Button from '~/UI/Button';
import iconButtonStyles from '~/UI/Button/icon.less';
import linkButtonStyles from '~/UI/Button/link.less';
import primaryIconButtonStyles from '~/UI/Button/primary-icon.less';
import purpleMicroButtonStyles from '~/UI/Button/purple-micro-icon.less';
import secondarySmallButtonStyles from '~/UI/Button/secondary-small.less';
import whiteMicroButtonStyles from '~/UI/Button/white-micro-icon.less';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Heading from '~/UI/Heading/Heading';
import Icon from '~/UI/Icon/Icon';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';

import styles from './styles.less';

interface Props {
	group: UserProductGroup;
	isUpdatingNotification: boolean;
	currency: string | null;
	formatDate(date: Date, includeYear?: boolean, locale?: string[]): string;
	onHistoryClick(): void;
	onExchangeRateClick(): void;
	onCreateListClick(): void;
	onNotificationSettingsClick(): void;
	onCopy(): void;
	onRename(): void;
	onDelete(): void;
}

interface State {
	popover: 'options' | 'download' | null;
}

export default class Header extends PureComponent<Props, State> {
	state = { popover: null };

	optionsPopoverContainerElement: HTMLSpanElement | null = null;

	downloadPopoverContainerElement: HTMLSpanElement | null = null;

	renderModule(group: UserProductGroup) {
		if (group.hasCzMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='CZ' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasCzReimbursementModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='CZ' />
					</span>{' '}
					Reimbursement
				</strong>
			);
		}
		if (group.hasSkMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='SK' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasAeMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='AE' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasSiMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='SI' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasHrMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='HR' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasBgMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='BG' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasRoMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='RO' />
					</span>{' '}
					Max Price
				</strong>
			);
		}
		if (group.hasLtMaxPriceModule()) {
			return (
				<strong data-testid='group-module'>
					<span className={styles.countryIconContainer}>
						<CountryIcon code='LT' />
					</span>{' '}
					Max Price
				</strong>
			);
		}

		return null;
	}

	renderOptionsButton(group: UserProductGroup) {
		const {
			props: { onCopy, onRename, onDelete },
			state: { popover },
		} = this;

		const optionsButton = (
			<div>
				<Button
					styles={iconButtonStyles}
					onClick={() => this.setState({ popover: popover === 'options' ? null : 'options' })}
				>
					<Icon name='options-white'>options</Icon>
				</Button>
			</div>
		);

		return (
			<span className={styles.optionButtonContainer} ref={(ref) => (this.optionsPopoverContainerElement = ref)}>
				<LayoutConstraint reference={optionsButton}>
					{popover === 'options' ? (
						<ProductGroupOptionsPopover
							onClose={this.handleCloseOptionsPopover}
							onCopy={() => this.optionSelected(onCopy)}
							onDelete={() => this.optionSelected(onDelete)}
							onRename={() => this.optionSelected(onRename)}
						/>
					) : null}
				</LayoutConstraint>
			</span>
		);
	}

	optionSelected = (action: () => void) => {
		this.setState({ popover: null });
		action();
	};

	handleCloseOptionsPopover = (event: MouseEvent) => {
		if (
			this.optionsPopoverContainerElement &&
			event.target !== this.downloadPopoverContainerElement &&
			event.target instanceof HTMLElement &&
			!this.optionsPopoverContainerElement.contains(event.target)
		) {
			this.setState({ popover: null });
		}
	};

	renderDownloadButton(group: UserProductGroup) {
		const {
			state: { popover },
			props: { currency },
		} = this;

		const downloadButton = (
			<Button
				styles={primaryIconButtonStyles}
				onClick={() => this.setState({ popover: popover === 'download' ? null : 'download' })}
			>
				<Icon name='download'>download</Icon> Download
			</Button>
		);

		return (
			<span className={styles.exportButtonContainer} ref={(ref) => (this.downloadPopoverContainerElement = ref)}>
				<LayoutConstraint reference={downloadButton}>
					{popover === 'download' ? (
						<ProductGroupDownloadPopover currency={currency} group={group} onClose={this.handleCloseDownloadPopover} />
					) : null}
				</LayoutConstraint>
			</span>
		);
	}

	handleCloseDownloadPopover = (event: MouseEvent) => {
		if (
			this.downloadPopoverContainerElement &&
			event.target !== this.downloadPopoverContainerElement &&
			event.target instanceof HTMLElement &&
			!this.downloadPopoverContainerElement.contains(event.target)
		) {
			this.setState({ popover: null });
		}
	};

	render() {
		const {
			group,
			isUpdatingNotification,
			formatDate,
			onHistoryClick,
			onExchangeRateClick,
			onCreateListClick,
			onNotificationSettingsClick,
		} = this.props;
		const exchangeRates = group.exchangeRates.settings;

		return (
			<Fragment>
				<Breadcrumbs>
					<BreadcrumbsItem to='/product-groups/#product-list'>Product List</BreadcrumbsItem>
					{group.parent ? (
						<BreadcrumbsItem to={`/product-groups/${encodeURIComponent(group.parent.id)}`}>
							{group.parent.name}
						</BreadcrumbsItem>
					) : null}
					<BreadcrumbsItem>{group.name}</BreadcrumbsItem>
				</Breadcrumbs>

				<div className={styles.header}>
					<div className={styles.in}>
						<div className={styles.meta}>
							<p className={styles.metaLeft}>{this.renderModule(group)}</p>
							<p className={styles.metaRight}>
								Last product added/removed on <strong>{formatDate(group.itemsUpdatedTime)}</strong>
							</p>
						</div>
						<Heading level={6}>{group.name}</Heading>

						<div className={styles.actions}>
							{this.renderDownloadButton(group)}

							{group.hasNotificationSettingsLink() ? (
								<span
									className={
										isUpdatingNotification
											? styles.updatingNotificationSettingsButton
											: styles.notificationSettingsButton
									}
								>
									<Button styles={iconButtonStyles} onClick={onNotificationSettingsClick}>
										<Icon name={group.notification.settings.isDisabled() ? 'bell-white' : 'bell-white-full'}>
											notifications
										</Icon>
									</Button>
								</span>
							) : null}

							{this.renderOptionsButton(group)}

							{group.hasProductsHistory() ? (
								<span className={styles.linkButton}>
									<Button styles={linkButtonStyles} onClick={onHistoryClick}>
										History
									</Button>
								</span>
							) : null}

							<span className={styles.buttonHr} />

							<Button
								styles={
									exchangeRates.date || exchangeRates.customExchangeRates
										? purpleMicroButtonStyles
										: whiteMicroButtonStyles
								}
								onClick={onExchangeRateClick}
							>
								<Icon
									name={exchangeRates.date || exchangeRates.customExchangeRates ? 'exchange-white' : 'exchange-dark'}
								>
									exchange
								</Icon>
								{exchangeRates.name}
								{exchangeRates.date ? (
									<>
										{' '}
										/{' '}
										<RateTimePeriod
											averageLabel='avg.'
											date={exchangeRates.date}
											fromLabel=''
											timePeriod={exchangeRates.timePeriod}
										/>
									</>
								) : null}
							</Button>

							{group.isProductList() ? (
								<div className={styles.saveActivityIndicator}>
									{<SaveActivityIndicator groupId={group.id} hideAfterMilliseconds={3000} />}
								</div>
							) : null}
						</div>

						<div className={styles.buttonsPanel}>
							{group.isGroup() ? (
								<div className={styles.buttonsPanelContent}>
									<Button styles={secondarySmallButtonStyles} onClick={onCreateListClick}>
										Create list
									</Button>
								</div>
							) : null}
						</div>
					</div>
				</div>
			</Fragment>
		);
	}
}
